import type { ElementFingerprint } from './types';

/**
 * Captures a complete fingerprint of a DOM element for fuzzy matching
 */
export function captureFingerprint(element: Element): ElementFingerprint {
  return {
    selector: generateSelector(element),
    textContent: normalizeText(element.textContent || ''),
    attributes: {
      id: element.getAttribute('id'),
      class: element.getAttribute('class'),
      dataTestid: element.getAttribute('data-testid'),
      src: element.getAttribute('src'),
      href: element.getAttribute('href'),
      alt: element.getAttribute('alt'),
      ariaLabel: element.getAttribute('aria-label'),
    },
    tagName: element.tagName,
    ariaRole: element.getAttribute('role'),
    parentContext: captureParentContext(element),
    siblingContext: captureSiblingContext(element),
  };
}

/**
 * Generates a unique CSS selector for an element
 */
export function generateSelector(element: Element): string {
  // Prefer ID if available
  if (element.id) {
    return `#${CSS.escape(element.id)}`;
  }

  // Use data-testid if available
  const testId = element.getAttribute('data-testid');
  if (testId) {
    return `[data-testid="${CSS.escape(testId)}"]`;
  }

  // Build path from root
  const path: string[] = [];
  let current: Element | null = element;

  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();

    // Add class if available
    if (current.classList.length > 0) {
      selector += `.${Array.from(current.classList).map(c => CSS.escape(c)).join('.')}`;
    }

    // Add nth-child if there are siblings with same tag
    const parent: Element | null = current.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter(
        (el): el is Element => el.tagName === current!.tagName
      );

      if (siblings.length > 1) {
        const index = siblings.indexOf(current) + 1;
        selector += `:nth-child(${index})`;
      }
    }

    path.unshift(selector);
    current = parent;
  }

  return path.join(' > ');
}

/**
 * Normalizes text content by collapsing whitespace and truncating
 */
export function normalizeText(text: string, maxLength = 500): string {
  // Collapse all whitespace to single spaces
  const normalized = text
    .replace(/\s+/g, ' ')
    .trim();

  // Truncate if needed
  if (normalized.length > maxLength) {
    return normalized.substring(0, maxLength);
  }

  return normalized;
}

/**
 * Captures context from parent elements (headings, sections)
 */
function captureParentContext(element: Element): string | null {
  let current = element.parentElement;

  // Look for nearest heading or meaningful parent
  while (current) {
    // Check for headings
    if (/^H[1-6]$/.test(current.tagName)) {
      return normalizeText(current.textContent || '', 200);
    }

    // Check for sections with headings
    const heading = current.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) {
      return normalizeText(heading.textContent || '', 200);
    }

    current = current.parentElement;

    // Don't go beyond body
    if (current === document.body) {
      break;
    }
  }

  return null;
}

/**
 * Captures context from sibling elements
 */
function captureSiblingContext(element: Element): string | null {
  const siblings: string[] = [];

  // Get previous sibling text
  const prevSibling = element.previousElementSibling;
  if (prevSibling) {
    const prevText = normalizeText(prevSibling.textContent || '', 100);
    if (prevText) {
      siblings.push(prevText);
    }
  }

  // Get next sibling text
  const nextSibling = element.nextElementSibling;
  if (nextSibling) {
    const nextText = normalizeText(nextSibling.textContent || '', 100);
    if (nextText) {
      siblings.push(nextText);
    }
  }

  return siblings.length > 0 ? siblings.join(' | ') : null;
}
